import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

export type ModelStatus = 'not_started' | 'running' | 'fail' | 'success';

export type ModelListItem = {
  id: string;
  modelName: string;
  status: ModelStatus;
  createdAt: string;
  kpiMetric: string | null;
  kpiCategory: string | null;
  mediaPlatforms: string[];
  campaignCount: number;
  quickRun: boolean;
};

export type ModelCampaign = {
  campaignId: string;
  campaignName: string | null;
  blacklist: boolean;
  groupId: string | null;
  groupName: string | null;
};

export type ModelDetails = {
  id: string;
  modelName: string;
  status: ModelStatus;
  createdAt: string;
  kpiSource: string | null;
  kpiMetric: string | null;
  kpiCategory: string | null;
  mediaPlatforms: string[];
  campaigns: ModelCampaign[];
};

type ModelsListResponse = {
  data: ModelListItem[];
};

type ModelDetailsResponse = {
  data: ModelDetails;
};

const modelsQueryKey = (customer: string) =>
  ['useModelsQuery', customer] as const;

const modelDetailsQueryKey = (customer: string, modelId: string) =>
  ['useModelDetailsQuery', customer, modelId] as const;

export function useModelsQuery(customer: string) {
  const queryId = useMemo(() => modelsQueryKey(customer), [customer]);

  return useQuery(
    queryId,
    async () => {
      const response = await request.get<ModelsListResponse>(
        '/data/models-list',
        { params: { customer } },
      );
      return response.data?.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer),
    },
  );
}

export function useModelDetailsQuery(customer: string, modelId: string | null) {
  const queryId = useMemo(
    () => modelDetailsQueryKey(customer, modelId ?? ''),
    [customer, modelId],
  );

  return useQuery(
    queryId,
    async () => {
      if (!modelId) return null;
      const response = await request.get<ModelDetailsResponse>('/data/model', {
        params: { customer, model_id: modelId },
      });
      return response.data?.data ?? null;
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer) && Boolean(modelId),
    },
  );
}

type UpdateModelVariables = {
  modelId: string;
  modelName: string;
  kpiSource: string;
  kpiMetric: string;
  kpiCategory: string | null;
  selectedMedia: string[];
  campaignSelections: Record<string, boolean>;
};

type DeleteModelVariables = {
  modelId: string;
};

export function useUpdateModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ modelId, ...data }: UpdateModelVariables) => {
      const response = await request.put<ModelDetailsResponse>(
        '/data/model',
        data,
        { params: { customer, model_id: modelId } },
      );
      return response.data?.data;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
        queryClient.invalidateQueries(
          modelDetailsQueryKey(customer, variables.modelId),
        );
      },
    },
  );
}

export function useDeleteModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ modelId }: DeleteModelVariables) => {
      const response = await request.delete<{ data: boolean }>('/data/model', {
        params: { customer, model_id: modelId },
      });
      return response.data?.data ?? false;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
      },
    },
  );
}

export type CreateModelInput = {
  modelName: string;
  kpiSource: string;
  kpiMetric: string;
  kpiCategory: string | null;
  selectedMedia: string[];
  campaignSelections: Record<string, boolean>;
};

type CreateModelVariables = {
  models: CreateModelInput[];
};

type CreateModelResponse = {
  success: boolean;
  models: Array<{
    userModelName: string;
    quick: {
      modelId: string;
      modelName: string;
      jobId: string;
      jobName: string;
    };
    full: {
      modelId: string;
      modelName: string;
      jobId: string;
      jobName: string;
    };
  }>;
  totalModels: number;
  message: string;
};

export function useCreateModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: CreateModelVariables) => {
      const response = await request.post<CreateModelResponse>(
        '/data/model',
        variables,
        { params: { customer } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
      },
    },
  );
}

export type ModelStatusResponse = {
  modelId: string;
  status: ModelStatus;
  startTime: string | null;
  lastUpdate: string | null;
  progress: number;
  iterationsDone: number;
  iterationsTotal: number;
  estimatedTimeRemaining: number | null;
  errorMessage: string | null;
};

export function useModelStatusQuery(
  customer: string,
  modelId: string | null,
  refetchInterval: number | false = 5000,
) {
  return useQuery(
    ['modelStatus', customer, modelId],
    async () => {
      const response = await request.get<{ data: ModelStatusResponse }>(
        '/data/models',
        { params: { customer, modelId } },
      );
      return response.data?.data ?? null;
    },
    {
      enabled: Boolean(customer) && Boolean(modelId),
      refetchInterval,
      staleTime: 0,
    },
  );
}

export function useModelsListPolling(
  customer: string,
  refetchInterval: number | false = 5000,
) {
  return useQuery(
    ['modelsListPolling', customer],
    async () => {
      const response = await request.get<ModelsListResponse>(
        '/data/models-list',
        { params: { customer } },
      );
      return response.data?.data ?? [];
    },
    {
      enabled: Boolean(customer),
      refetchInterval,
      staleTime: 0,
    },
  );
}
